import * as api from "../generated-api";
import { apiCall, apiCallAsync } from "../common";
import { AxiosError } from "axios";
import { processUnexpectedError } from "../errors";

export const getDealInvestor = (
  orgId: number,
  dealId: number,
  investorId: number,
  successCallback?: (investor: api.DealInvestor) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealInvestor>(
    () => {
      return api.getDealInvestor(orgId, dealId, investorId);
    },
    successCallback,
    errorCallback
  );
};

export const updateDealInvestor = (
  orgId: number,
  dealId: number,
  investorId: number,
  data: api.DealInvestorUpdate,
  successCallback?: (investors: api.DealInvestor) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealInvestor>(
    () => {
      return api.updateDealInvestor(orgId, dealId, investorId, data);
    },
    successCallback,
    errorCallback
  );
};

export const createDealInvestorWithMember = (
  orgId: number,
  dealId: number,
  investorWithMember: api.DealInvestorAndPersonCreate,
  successCallback?: (investor: api.DealInvestor) => void,
  errorCallback?: (error: AxiosError) => void
) => {
  apiCall<api.DealInvestor>(
    () => {
      return api.createDealInvestorWithMember(
        orgId,
        dealId,
        investorWithMember
      );
    },
    successCallback,
    errorCallback
  );
};

export const getDealInvestors = (
  orgId: number,
  dealId: number,
  successCallback?: (investors: api.DealInvestor[]) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealInvestor[]>(
    () => {
      return api.getDealInvestors(orgId, dealId);
    },
    successCallback,
    errorCallback
  );
};

export type DealInvestorsLegalEntitiesMap = Map<
  number,
  api.DealLegalEntityInvestment[]
>;

export const getDealInvestorsLegalEntitiesForInvestorsAsync = async (
  orgId: number,
  dealId: number,
  investorIds: number[],
  errorCallback?: (error: Error) => void
) => {
  const fetchedDealInvestorsLegalEntities = await Promise.all(
    investorIds.map(async (investorId) => {
      return await apiCallAsync<api.DealLegalEntityInvestment[]>(() => {
        return api.getDealInvestorDealLegalEntityInvestments(
          orgId,
          dealId,
          investorId
        );
      }, errorCallback);
    })
  );

  const updatedDealInvestorsLegalEntitiesMap = new Map<
    number,
    api.DealLegalEntityInvestment[]
  >();

  fetchedDealInvestorsLegalEntities.forEach((result) => {
    if (result[0] && result[0].investorId) {
      updatedDealInvestorsLegalEntitiesMap.set(result[0].investorId, result);
    }
  });

  return updatedDealInvestorsLegalEntitiesMap;
};

export const getDealInvestorDealLegalEntityInvestments = (
  orgId,
  dealId,
  investorId,
  successCallback?: (
    dealLegalEntityInvestments: api.DealLegalEntityInvestment[]
  ) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealLegalEntityInvestment[]>(
    () => {
      return api.getDealInvestorDealLegalEntityInvestments(
        orgId,
        dealId,
        investorId
      );
    },
    successCallback,
    errorCallback
  );
};

export const getAllDealLegalEntityInvestmentsForDeal = (
  orgId: number,
  dealId: number,
  successCallback?: (
    dealLegalEntityInvestments: api.DealLegalEntityInvestment[]
  ) => void,
  errorCallback?: (error: Error) => void
) => {
  getDealInvestors(
    orgId,
    dealId,
    (investors) => {
      if (investors && investors.length) {
        const ids = investors.map((investor) => {
          return investor.id;
        });

        getDealInvestorsLegalEntitiesForInvestorsAsync(
          orgId,
          dealId,
          ids,
          errorCallback
        )
          .then((legalEntitiesById) => {
            const valuesIterator = legalEntitiesById.values();
            const legalEntities = Array.from(valuesIterator);
            successCallback(legalEntities.flat());
          })
          .catch((error) => {
            if (errorCallback) {
              errorCallback(error);
            } else {
              processUnexpectedError(error);
            }
          });
      } else {
        successCallback([]);
      }
    },
    errorCallback
  );
};

export const getAllDealLegalEntityInvestmentsForDealAsync = async (
  orgId: number,
  dealId: number,
  errorCallback?: (error: Error) => void
) => {
  const investors = await apiCallAsync<api.DealInvestor[]>(() => {
    return api.getDealInvestors(orgId, dealId);
  }, errorCallback);

  if (investors && investors.length) {
    const results = await Promise.all(
      investors.map(async (investor) => {
        const result = await api.getDealInvestorDealLegalEntityInvestments(
          orgId,
          dealId,
          investor.id
        );

        return result.data;
      })
    );

    return results.flat();
  } else {
    return [];
  }
};

export const getDealInvestorsMembersAsync = async (
  orgId: number,
  dealId: number,
  investorIds: number[],
  errorCallback?: (error: Error) => void
) => {
  const fetchedDealInvestorsMembers = await Promise.all(
    investorIds.map(async (investorId) => {
      return await apiCallAsync<api.DealLegalEntity[]>(() => {
        return api.getDealInvestorMembers(orgId, dealId, investorId);
      }, errorCallback);
    })
  );

  return fetchedDealInvestorsMembers.flat();
};

export const getDealInvestorMembers = (
  orgId: number,
  dealId: number,
  investorId: number,
  successCallback?: (investors: api.DealLegalEntity[]) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealLegalEntity[]>(
    () => {
      return api.getDealInvestorMembers(orgId, dealId, investorId);
    },
    successCallback,
    errorCallback
  );
};

export const addNewPersonAsDealInvestorMember = (
  orgId: number,
  dealId: number,
  investorId: number,
  person: api.PersonCreate,
  successCallback?: (dealInvestorMember: api.DealLegalEntity) => void,
  errorCallback?: (error: AxiosError) => void
) => {
  apiCall<api.DealLegalEntity>(
    () => {
      return api.addNewPersonAsDealInvestorMember(
        orgId,
        dealId,
        investorId,
        person
      );
    },
    successCallback,
    errorCallback
  );
};

export const addExistingPersonAsDealInvestorMember = (
  orgId: number,
  dealId: number,
  investorId: number,
  addPerson: api.AddPersonAsDealInvestorMember,
  successCallback?: (dealInvestorMember: api.DealLegalEntity) => void,
  errorCallback?: (error: AxiosError) => void
) => {
  apiCall<api.DealLegalEntity>(
    () => {
      return api.addExistingPersonAsDealInvestorMember(
        orgId,
        dealId,
        investorId,
        addPerson
      );
    },
    successCallback,
    errorCallback
  );
};

export const removeDealInvestorMember = (
  orgId: number,
  dealId: number,
  investorId: number,
  dealInvestorMemberId: number,
  successCallback?: () => void,
  errorCallback?: (error: AxiosError) => void
) => {
  apiCall(
    () => {
      return api.removeDealInvestorMember(
        orgId,
        dealId,
        investorId,
        dealInvestorMemberId
      );
    },
    successCallback,
    errorCallback
  );
};

export const updateDealLegalEntityInvestment = (
  orgId: number,
  dealId: number,
  investorId: number,
  dealLegalEntityInvestmentId: number,
  data: api.DealLegalEntityInvestmentUpdate,
  successCallback?: (
    dealLegalEntityInvestment: api.DealLegalEntityInvestment
  ) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealLegalEntityInvestment>(
    () => {
      return api.updateDealLegalEntityInvestment(
        orgId,
        dealId,
        investorId,
        dealLegalEntityInvestmentId,
        data
      );
    },
    successCallback,
    errorCallback
  );
};

export const getInvestmentDealLegalEntitiesForDealAndDealInvestor = (
  orgId: number,
  dealId: number,
  dealInvestorId: number,
  successCallback?: (dealLegalEntityInvestments: api.DealLegalEntity[]) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealLegalEntity[]>(
    () => {
      return api.getInvestmentDealLegalEntitiesForDealAndDealInvestor(
        orgId,
        dealId,
        dealInvestorId
      );
    },
    successCallback,
    errorCallback
  );
};
